import React from 'react';
import { Box } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { Chip } from 'cprime-foundational-components';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { formatDataValue } from 'utils/formatFns';
import { generateTestId } from 'utils/general';
import { chipColorMap } from 'utils/colorMappers';
import { getChipSx } from '../utils/utils';
import { getNOMValue } from './companyCarFields';

export const vehicleAttrsFields = {
  dateOfFirstUse: {
    label: 'Date of First Use',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.dateOfFirstUseFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  dofu: {
    label: 'DOFU',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.dateOfFirstUseFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  exportIndicator: {
    label: 'Export Indicator',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.exportIndicator,
    formatValue: (value, options = {}) => {
      if (!options.isVehicleWithLimitedFeatures || !value) {
        return null;
      }

      return (
        <TooltipWrapper tooltipText="Export Indicator">
          <Box>
            <Chip
              data-testid={generateTestId('VehicleRecord', 'VehicleDetailsHeader', 'export-chip')}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value="EXPORT"
                    fieldId="vehicle-hero-export-indicator"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={
                options.isMobile ? (
                  <LocationOn color={chipColorMap.lifecycleChipBackground} />
                ) : null
              }
              color={chipColorMap.lifecycleChipText}
              backgroundColor={chipColorMap.lifecycleChipBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  katashikiDescription: {
    label: 'Katashiki Description',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.katashikiDescription,
    formatValue: (value) => formatDataValue({ value }),
  },
  orderMonth: {
    label: 'Order Month',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.orderMonthNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  orderNumber: {
    label: 'Order Number',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.orderNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  orderRank: {
    label: 'Order Rank',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.orderRan,
    formatValue: (value) => formatDataValue({ value }),
  },
  orderType: {
    label: 'Order Type',
    getValue: (data) => ({
      orderType: data?.generalInfo?.vehicleAttrs?.orderType,
      orderTypeDescription: data?.generalInfo?.vehicleAttrs?.orderTypeDescription,
    }),
    formatValue: ({ orderType, orderTypeDescription }) => {
      if (!orderType && !orderTypeDescription) {
        return null;
      }
      if (orderType && !orderTypeDescription) {
        return orderType;
      }
      const formattedType = formatDataValue({ value: orderType });
      const formattedDescription = formatDataValue({ value: orderTypeDescription });
      return `${formattedType} | ${formattedDescription}`;
    },
  },
  originalEDNumber: {
    label: 'Original ED Number',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.originalEdNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  pricedUnpriced: {
    label: 'Priced/Unpriced',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.unpricedIndicator,
    formatValue: (value) => (value ? 'UNPRICED' : 'PRICED'),
  },
  sellToDealerCode: {
    label: 'Sell To Dealer Code',
    getValue: (data) => ({
      dealerCode: data?.generalInfo?.vehicleAttrs?.sellToDealerCode,
      dealerName: data?.generalInfo?.vehicleAttrs?.sellToDealerName,
    }),
    formatValue: ({ dealerCode, dealerName }) => {
      const formattedCode = formatDataValue({ value: dealerCode });
      const formattedName = formatDataValue({ value: dealerName });
      return `${formattedCode} | ${formattedName}`;
    },
  },
  sellToRegion: {
    label: 'Sell To Region',
    getValue: (data) => ({
      regionCode: data?.generalInfo?.vehicleAttrs?.sellToRegionCode,
      regionName: data?.generalInfo?.vehicleAttrs?.sellToRegionName,
    }),
    formatValue: ({ regionCode, regionName }) => {
      const formattedCode = formatDataValue({ value: regionCode });
      const formattedName = formatDataValue({ value: regionName });
      return `${formattedCode} | ${formattedName}`;
    },
  },
  shipToDealerNewOrderManagement: {
    label: 'Ship to Dealer',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      dealerCode: data?.generalInfo?.vehicleAttrs?.shipToDealerCode,
      dealerName: data?.generalInfo?.vehicleAttrs?.shipToDealerName,
    }),
    formatValue: (value) => {
      const dealerCode = formatDataValue({ value: value.dealerCode });
      const dealerName = formatDataValue({ value: value.dealerName });
      const combinedValue = `${dealerCode} | ${dealerName}`;

      return getNOMValue({
        TMLI: value.TMLI,
        value: combinedValue,
      });
    },
  },
  shipToRegion: {
    label: 'Ship To Region',
    getValue: (data) => ({
      regionCode: data?.generalInfo?.vehicleAttrs?.shipToRegionCode,
      regionName: data?.generalInfo?.vehicleAttrs?.shipToRegionName,
    }),
    formatValue: ({ regionCode, regionName }) => {
      const formattedCode = formatDataValue({ value: regionCode });
      const formattedName = formatDataValue({ value: regionName });
      return `${formattedCode} | ${formattedName}`;
    },
  },
  unpricedIndicator: {
    label: 'Unpriced Indicator',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.unpricedIndicator,
    formatValue: (value) => (value ? 'Y' : 'N'),
  },
  vds: {
    label: 'VDS',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.vds,
    formatValue: (value) => formatDataValue({ value }),
  },
  wmi: {
    label: 'WMI',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.wmi,
    formatValue: (value) => formatDataValue({ value }),
  },
};
